import { forwardRef } from 'react'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Stepper — этапы прогресса (`.steps`/`.step` макета). Каждый этап: done/current/upcoming с
 * соответствующими стилями круга и соединителя из токенов. Текущий помечен aria-current="step";
 * статус передаётся не только цветом (галочка у done, номер у остальных, sr-only подпись состояния).
 */
export interface StepperProps {
  steps: string[]
  /** Индекс текущего этапа (0..n-1). Этапы до него — пройдены, после — предстоящие. */
  current: number
  className?: string
}

const STATE_LABEL = {
  done: 'пройден',
  current: 'текущий',
  upcoming: 'предстоит',
} as const

export const Stepper = forwardRef<HTMLOListElement, StepperProps>(
  ({ steps, current, className }, ref) => (
    <ol ref={ref} className={cn('flex', className)}>
      {steps.map((label, i) => {
        const state = i < current ? 'done' : i === current ? 'current' : 'upcoming'
        const isLast = i === steps.length - 1
        return (
          <li
            key={i}
            aria-current={state === 'current' ? 'step' : undefined}
            className={cn(
              'relative flex flex-1 flex-col items-center gap-1.5 text-center text-12',
              state === 'current' ? 'font-medium text-accent-txt' : 'text-txt-tertiary',
            )}
          >
            <div className="relative flex h-step w-full items-center justify-center">
              {!isLast ? (
                <span
                  aria-hidden
                  className={cn(
                    'absolute left-1/2 top-1/2 h-0.5 w-full -translate-y-1/2',
                    state === 'done' ? 'bg-green' : 'bg-line',
                  )}
                />
              ) : null}
              <span
                className={cn(
                  'relative z-10 flex size-step items-center justify-center rounded-round text-12 font-semibold',
                  state === 'done' && 'bg-green text-white',
                  state === 'current' && 'bg-accent text-white',
                  state === 'upcoming' && 'bg-gray-bg text-txt-secondary',
                )}
              >
                {state === 'done' ? (
                  <Check className="size-3.5 stroke-current" aria-hidden />
                ) : (
                  i + 1
                )}
              </span>
            </div>
            <span>
              {label}
              <span className="sr-only"> — {STATE_LABEL[state]}</span>
            </span>
          </li>
        )
      })}
    </ol>
  ),
)
Stepper.displayName = 'Stepper'
